// src/pages/admin/CompliancePage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminComplianceApi } from '@/api/services'
import toast from 'react-hot-toast'
import { FileText, Truck, IndianRupee, Send, XCircle, Clock, CheckCircle, AlertTriangle } from 'lucide-react'

const STATUS_COLORS = {
  draft:     'bg-gray-100 text-gray-700',
  generated: 'bg-blue-100 text-blue-700',
  active:    'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  expired:   'bg-amber-100 text-amber-700',
  collected: 'bg-blue-100 text-blue-700',
  remitted:  'bg-green-100 text-green-700',
  filed:     'bg-purple-100 text-purple-700',
}

export default function CompliancePage() {
  const [tab, setTab] = useState('eway')

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Legal & Compliance</h1>

      <div className="flex gap-2 border-b">
        <button
          onClick={() => setTab('eway')}
          className={`px-4 py-2 font-medium border-b-2 transition ${tab === 'eway' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          <Truck className="inline w-4 h-4 mr-1" /> E-Way Bills
        </button>
        <button
          onClick={() => setTab('tcs')}
          className={`px-4 py-2 font-medium border-b-2 transition ${tab === 'tcs' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          <IndianRupee className="inline w-4 h-4 mr-1" /> TCS (194-O)
        </button>
      </div>

      {tab === 'eway' ? <EwayBillsTab /> : <TcsTab />}
    </div>
  )
}

/* ── E-Way Bills Tab ──────────────────────────────────────────────────── */
function EwayBillsTab() {
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('')
  const { data, isLoading } = useQuery({
    queryKey: ['admin-eway-bills', statusFilter],
    queryFn: () => adminComplianceApi.ewayBills({ status: statusFilter || undefined }).then(r => r.data),
  })

  const submitMut = useMutation({
    mutationFn: (id) => adminComplianceApi.ewayBillSubmit(id),
    onSuccess: () => { toast.success('E-way bill submitted to NIC'); qc.invalidateQueries({ queryKey: ['admin-eway-bills'] }) },
    onError: (e) => toast.error(e.response?.data?.message || 'Submit failed'),
  })

  const cancelMut = useMutation({
    mutationFn: ({ id, reason }) => adminComplianceApi.ewayBillCancel(id, { reason }),
    onSuccess: () => { toast.success('E-way bill cancelled'); qc.invalidateQueries({ queryKey: ['admin-eway-bills'] }) },
    onError: (e) => toast.error(e.response?.data?.message || 'Cancel failed'),
  })

  const bills = data?.data ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">E-way bills for goods movement above Rs.50,000</p>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="generated">Generated</option>
          <option value="active">Active</option>
          <option value="cancelled">Cancelled</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : bills.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No e-way bills found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-600">
                <th className="px-4 py-3 font-medium">Order</th>
                <th className="px-4 py-3 font-medium">E-Way Bill #</th>
                <th className="px-4 py-3 font-medium">Supplier</th>
                <th className="px-4 py-3 font-medium text-right">Amount</th>
                <th className="px-4 py-3 font-medium">Valid Until</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {bills.map(bill => (
                <tr key={bill.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">#{bill.order?.order_number}</td>
                  <td className="px-4 py-3">{bill.eway_bill_number || '—'}</td>
                  <td className="px-4 py-3">{bill.supplier?.name || '—'}</td>
                  <td className="px-4 py-3 text-right">Rs.{Number(bill.total_value || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3">
                    {bill.valid_until ? new Date(bill.valid_until).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[bill.status] || 'bg-gray-100 text-gray-600'}`}>
                      {bill.status?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {bill.status === 'draft' && (
                      <button
                        onClick={() => submitMut.mutate(bill.id)}
                        disabled={submitMut.isPending}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                      >
                        <Send className="inline w-3.5 h-3.5 mr-0.5" /> Submit
                      </button>
                    )}
                    {(bill.status === 'active' || bill.status === 'generated') && (
                      <button
                        onClick={() => {
                          const reason = prompt('Reason for cancellation:')
                          if (reason) cancelMut.mutate({ id: bill.id, reason })
                        }}
                        disabled={cancelMut.isPending}
                        className="text-red-600 hover:text-red-800 text-xs font-medium"
                      >
                        <XCircle className="inline w-3.5 h-3.5 mr-0.5" /> Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/* ── TCS Tab ──────────────────────────────────────────────────────────── */
function TcsTab() {
  const qc = useQueryClient()
  const [fy, setFy] = useState('')

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['admin-tcs-summary', fy],
    queryFn: () => adminComplianceApi.tcsSummary({ financial_year: fy || undefined }).then(r => r.data),
  })

  const { data: txnData, isLoading: txnLoading } = useQuery({
    queryKey: ['admin-tcs-transactions', fy],
    queryFn: () => adminComplianceApi.tcsTransactions({ financial_year: fy || undefined }).then(r => r.data),
  })

  const remitMut = useMutation({
    mutationFn: (d) => adminComplianceApi.tcsRemit(d),
    onSuccess: () => {
      toast.success('TCS marked as remitted')
      qc.invalidateQueries({ queryKey: ['admin-tcs'] })
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Remit failed'),
  })

  const stats = summary?.data ?? {}
  const transactions = txnData?.data ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">TCS @ 1% under Section 194-O (e-commerce marketplace)</p>
        <input
          type="text"
          placeholder="FY e.g. 2025-26"
          value={fy}
          onChange={e => setFy(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm w-36"
        />
      </div>

      {/* Summary cards */}
      {!summaryLoading && stats.total_collected !== undefined && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SummaryCard
            icon={<IndianRupee className="w-5 h-5 text-blue-500" />}
            label="Total Collected"
            value={`Rs.${Number(stats.total_collected || 0).toLocaleString('en-IN')}`}
          />
          <SummaryCard
            icon={<CheckCircle className="w-5 h-5 text-green-500" />}
            label="Remitted to Govt"
            value={`Rs.${Number(stats.total_remitted || 0).toLocaleString('en-IN')}`}
          />
          <SummaryCard
            icon={<Clock className="w-5 h-5 text-amber-500" />}
            label="Pending Remittance"
            value={`Rs.${Number(stats.total_pending || 0).toLocaleString('en-IN')}`}
          />
        </div>
      )}

      {/* Quarterly breakdown */}
      {stats.quarterly && stats.quarterly.length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-semibold text-gray-800 mb-3">Quarterly Breakdown</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {stats.quarterly.map(q => (
              <div key={q.quarter} className="bg-gray-50 rounded p-3 text-center">
                <div className="text-xs text-gray-500 font-medium">{q.quarter}</div>
                <div className="text-lg font-bold text-gray-900">Rs.{Number(q.collected || 0).toLocaleString('en-IN')}</div>
                <div className="text-xs text-gray-500">
                  Remitted: Rs.{Number(q.remitted || 0).toLocaleString('en-IN')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transactions table */}
      <div className="bg-white rounded-lg border overflow-x-auto">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">TCS Transactions</h3>
        </div>
        {txnLoading ? (
          <div className="py-8 text-center text-gray-400">Loading...</div>
        ) : transactions.length === 0 ? (
          <div className="py-8 text-center text-gray-400">No TCS transactions found</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-600">
                <th className="px-4 py-3 font-medium">Order</th>
                <th className="px-4 py-3 font-medium">Buyer</th>
                <th className="px-4 py-3 font-medium text-right">Order Amount</th>
                <th className="px-4 py-3 font-medium text-right">TCS (1%)</th>
                <th className="px-4 py-3 font-medium">Quarter</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {transactions.map(tx => (
                <tr key={tx.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">#{tx.order?.order_number || tx.order_id}</td>
                  <td className="px-4 py-3">{tx.buyer?.name || '—'}</td>
                  <td className="px-4 py-3 text-right">Rs.{Number(tx.order_amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-right font-medium">Rs.{Number(tx.tcs_amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3">{tx.quarter}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[tx.status] || 'bg-gray-100 text-gray-600'}`}>
                      {tx.status?.toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function SummaryCard({ icon, label, value }) {
  return (
    <div className="bg-white rounded-lg border p-4 flex items-center gap-3">
      <div className="p-2 bg-gray-50 rounded-lg">{icon}</div>
      <div>
        <div className="text-xs text-gray-500 font-medium">{label}</div>
        <div className="text-lg font-bold text-gray-900">{value}</div>
      </div>
    </div>
  )
}
